<template>
    <v-dialog v-model="show" max-width="400px">
        <v-card>
            <v-card-title>
                <span class="headline" v-if="isEdited">{{ $t("licenses.edit_license") }}</span>
                <span class="headline" v-else>{{ $t("licenses.add_license") }}</span>
            </v-card-title>

            <v-card-text>
                <v-form ref="form" v-model="valid" lazy-validation>
                    <v-container>
                        <v-row>
                            <v-col cols="12">
                                <v-text-field 
                                     autofocus 
                                     v-model="license.name" 
                                     :label="$t('licenses.name')"
                                     :rules="nameRules" 
                                     :placeholder="$t('licenses.name')"
                                    required>
                                </v-text-field>
                                <v-autocomplete 
                                    v-model="license.tenant_id" 
                                    :label="$t('licenses.client')"
                                    :rules="nameRules" 
                                    :items="clientList" item-text="name" 
                                    item-value="id"
                                    no-data-text="Zatím tu nejsou žádné záznamy" 
                                    @change="updateMaxDevices" 
                                    required>
                                </v-autocomplete>
                                <v-menu 
                                    v-model="startDateMenu" 
                                    :close-on-content-click="false"
                                    transition="scale-transition" 
                                    offset-y min-width="290px">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field v-model="formattedStartDate" :label="$t('licenses.valid_from')"
                                            readonly :rules="startDateRules" v-bind="attrs" v-on="on"
                                            prepend-icon="mdi-calendar"></v-text-field>
                                    </template>
                                    <v-date-picker v-model="datePickerStartDate"
                                        @input="handleStartDateChange"></v-date-picker>
                                </v-menu>

                                <v-menu v-model="endDateMenu" :close-on-content-click="false"
                                    transition="scale-transition" offset-y min-width="290px">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field v-model="formattedEndDate" :label="$t('licenses.valid_to')"
                                            readonly :rules="endDateRules" v-bind="attrs" v-on="on"
                                            prepend-icon="mdi-calendar"></v-text-field>
                                    </template>
                                    <v-date-picker v-model="datePickerEndDate" :min="datePickerStartDate"
                                        @input="handleEndDateChange"></v-date-picker>
                                </v-menu>
                                <v-text-field v-model="license.devices" type="number" min="1"
                                    :label="$t('licenses.devices')" :rules="devicesRules"></v-text-field>

                                <!-- <v-autocomplete v-model="license.oculusDevices"
                                    :items="matchOculus.length ? matchOculus : [{ id: 'default', name: $t('general.we_dont_have_data_yet') }]"
                                    item-text="name" item-value="id" :label="$t('licenses.oculus')" multiple chips
                                    :rules="matchOculus.length ? [...oculusRules] : []" :max-items="license.devices"
                                    :disabled="matchOculus.length === 0 && license.oculusDevices === 0" @input="handleOculusInput">
                                    <template v-slot:selection="{ item, index }">
                                        <v-chip v-if="index < 3" close @click:close="removeOculus(item)" small>
                                            {{ item.name }}
                                        </v-chip>
                                        <span v-if="index === 3" class="grey--text text-caption">
                                            (+{{ license.oculusDevices.length - 3 }} {{ $t('general.more') }})
                                        </span>
                                    </template>
                                </v-autocomplete> -->

                                <v-autocomplete v-model="license.modules" :items="modulesList" item-text="name"
                                    item-value="id" :label="$t('licenses.modules')" multiple chips
                                    :rules="modulesRules">
                                    <template v-slot:selection="{ item, index }">
                                        <v-chip v-if="index < 3" close @click:close="removeModule(item)" small>
                                            {{ item.name }}
                                        </v-chip>
                                        <span v-if="index === 3" class="grey--text text-caption">
                                            (+{{ license.modules.length - 3 }} {{ $t('general.more') }})
                                        </span>
                                    </template>
                                </v-autocomplete>
                            </v-col>
                        </v-row>
                        <v-row>
              <v-col cols="12" sm="5">
                <v-switch
                  color="green"
                  v-model="license.is_active"
                  :label="
                    license.is_active
                      ? $t('general.active')
                      : $t('general.not_active')
                  "
                ></v-switch>
              </v-col>
            </v-row>
                    </v-container>
                </v-form>
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" dark @click="submit" :disabled="!valid">
                    {{ $t("general.save") }}
                </v-btn>
                <v-btn @click.stop="show = false">
                    {{ $t("general.cancel") }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import axios from 'axios';

export default {
    name: "LicenseForm",
    props: {
        license: {
            type: Object,
            default: () => ({ name: '', client: '', tenant_id: '', valid_from: null, valid_to: null, devices: 1, modules: [], oculusDevices: [], is_active: null })
        },
        visible: Boolean,
        isEdited: Boolean,
    },
    data() {
        return {
            modulesList: [],
            oculusList: [],
            clientList: [],
            matchOculus: [],
            maxDevices: null,
            valid: true,
            startDateMenu: false,
            endDateMenu: false,
            datePickerStartDate: null,
            datePickerEndDate: null,

            nameRules: [
                v => !!v || this.$t("general.required"),
            ],
            startDateRules: [
                v => !!v || this.$t("general.required")
            ],
            endDateRules: [
                v => !!v || this.$t("general.required"),
                v => this.validateEndDate() || this.$t("licenses.end_date_after_start")
            ],
            devicesRules: [
                v => !!v || this.$t("general.required"),
                v => (v && parseInt(v) >= 1) || this.$t("licenses.min_devices", { min: 1 }),
                v => /^\d+$/.test(v) || this.$t("general.must_be_number"),
                // v => {
                //        if (this.maxDevices === null || this.maxDevices === undefined || this.maxDevices <= 0) {
                //          return true; 
                //        }
                //        const deviceCount = parseInt(v, 10); 

                //        if (isNaN(deviceCount) || deviceCount > this.maxDevices) {
                //         console.log(deviceCount, this.maxDevices)
                //         return this.$t("general.required", { max: this.maxDevices });
                //        }
                //        else {
                //          return true;
                //        }
                //     }
            ],
            modulesRules: [
                v => (v && v.length > 0) || this.$t("general.required")
            ],
            oculusRules: [
                v => (v && v.length > 0) || this.$t("general.required"),
                v => {
                    if (v && this.maxDevices && v.length != this.license.devices) return this.$t("licenses.not_equal")
                    return true;
                }
            ],
        };
    },

    computed: {
        show: {
            get() {
                return this.visible;
            },
            set(value) {
                if (!value) {
                    this.$emit("close");
                    
                }
            },
        },
        maxOculusSelection() {
            return Math.min(this.license.devices, this.oculusList.length);
        },
        formattedStartDate() {
            return this.formatDisplayDate(this.license.valid_from);
        },
        formattedEndDate() {
            return this.formatDisplayDate(this.license.valid_to);
        }
    },

    watch: {
        visible(val) {
            if (val) {
                this.initializeFormData();
                if (this.$refs.form) {
                    this.$refs.form.resetValidation();
                }
            }else{
                   this.$refs.form.resetValidation();
            }
        },
        license: {
              handler(newLicense, oldLicense) {
                   if (this.isEdited && this.visible) {
                    this.initializeFormData();
                }
            }
        },
        'license.devices'(newValue, oldValue) {
         this.license.oculusDevices = [];
         },
        datePickerStartDate(newVal) {
            console.log("datePickerStartDate", newVal)
            if (newVal && this.datePickerEndDate && newVal > this.datePickerEndDate) {
                this.datePickerEndDate = newVal;
                this.handleEndDateChange(newVal);
            }
        }
    },
    methods: {
        updateMaxDevices() {
            try {
                const selectedClient = this.clientList.find(client => client.id === this.license.tenant_id);
                const matchClient = this.oculusList.filter(e => e.tenant_id === selectedClient.tenant_id);
                this.maxDevices = matchClient.length;
                this.matchOculus = matchClient;
                if (this.isEdited && this.license.oculusDevices && this.license.oculusDevices.length > 0) {
                    if (!this.license.oculusDevices || !this.license.oculusDevices.length) {
                        this.license.oculusDevices = [...this.license.oculusDevices];
                    }
                }
            } catch (error) {
                console.error("Error max devices:", error);
                this.maxDevices = null;
            }
        },
        handleOculusInput() {
            if (this.matchOculus.length === 0) {
                this.license.oculusDevices = [];
                this.$nextTick(() => {
                    this.$emit('no-oculus-available');
                    
                });
            }
        },
        removeOculus(item) {
            const index = this.license.oculusDevices.findIndex(oculus => {
                if (typeof oculus === 'object') {
                    return oculus.id === item.id;
                }
                return oculus === item.id;
            });
            
            if (index !== -1) {
                this.license.oculusDevices.splice(index, 1);
            }
        },
        parseDate(dateString) {
            if (!dateString) return null;

            try {
                if (dateString instanceof Date) {
                    return dateString;
                }
                if (typeof dateString === 'string' && dateString.includes('-')) {
                    return new Date(dateString);
                }
                if (typeof dateString === 'string' && dateString.includes('.')) {
                    const parts = dateString.split('.');
                    let yearPart = parts[2];
                    let timePart = "00:00";
                    if (yearPart.includes(' ')) {
                        const yearAndTime = yearPart.split(' ');
                        yearPart = yearAndTime[0];
                        timePart = yearAndTime[1] || "00:00";
                    }

                    const day = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1;
                    const year = parseInt(yearPart, 10);

                    let hours = 0, minutes = 0;
                    if (timePart.includes(':')) {
                        const timeComponents = timePart.split(':');
                        hours = parseInt(timeComponents[0], 10);
                        minutes = parseInt(timeComponents[1], 10);
                    }
                    return new Date(year, month, day, hours, minutes);
                }
                return new Date(dateString);
            } catch (e) {
                console.error("Error parsing date:", e);
                return null;
            }
        },
        formatDisplayDate(dateValue) {
            if (!dateValue) {
                return '';
            }
            console.log("formatDisplayDate", dateValue);
            try {
                const date = this.parseDate(dateValue);
                if (!date || isNaN(date.getTime())) {
                    console.error("Invalid date:", dateValue);
                    return dateValue || '';
                }
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                return `${day}.${month}.${year} ${hours}:${minutes}`;
            } catch (e) {
                console.error("Error formatting date:", e);
                return dateValue || '';
            }
        },
        convertToDatePickerFormat(dateValue) {
            if (!dateValue) return null;
            try {
                console.log("convertToDatePickerFormat ", dateValue);
                const date = this.parseDate(dateValue);
                if (!date || isNaN(date.getTime())) {
                    console.error("Invalid date for picker:", dateValue);
                    return null;
                }
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            } catch (e) {
                console.error("Error converting date for picker:", e);
                return null;
            }
        },

        validateEndDate() {
            if (!this.license.valid_from || !this.license.valid_to) return true;
            try {
                const startDate = this.parseDate(this.license.valid_from);
                const endDate = this.parseDate(this.license.valid_to);
                if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                    return false;
                }
                const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
                const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
                return endDay >= startDay;
            } catch (e) {
                console.error('Error validating dates:', e);
                return false;
            }
        },
        handleStartDateChange(date) {
            this.startDateMenu = false;
            if (!date) return;
            const selectedDate = new Date(date);
            if (isNaN(selectedDate.getTime())) {
                console.error("Invalid start date selected:", date);
                return;
            }
            let hours = "00", minutes = "00";
            if (this.license.valid_from) {
                const currentDate = this.parseDate(this.license.valid_from);
                if (currentDate && !isNaN(currentDate.getTime())) {
                    hours = String(currentDate.getHours()).padStart(2, '0');
                    minutes = String(currentDate.getMinutes()).padStart(2, '0');
                }
            }
            selectedDate.setDate(selectedDate.getDate() + 1);
            const day = String(selectedDate.getDate()).padStart(2, '0');
            const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
            const year = selectedDate.getFullYear();
            this.$set(this.license, 'valid_from', `${day}.${month}.${year} ${hours}:${minutes}`);
            if (this.license.valid_to) {
                const endDate = this.parseDate(this.license.valid_to);
                const startDateOnly = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
                const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

                if (endDateOnly < startDateOnly) {
                    this.datePickerEndDate = date;
                    this.handleEndDateChange(date);
                }
            }
        },
        handleEndDateChange(date) {
            this.endDateMenu = false;
            console.log("handleEndDateChange", date)
            if (!date) return;

            const selectedDate = new Date(date);
            if (isNaN(selectedDate.getTime())) {
                console.error("Invalid end date selected:", date);
                return;
            }
            let hours = "23", minutes = "59";
            if (this.license.valid_to) {
                const currentDate = this.parseDate(this.license.valid_to);
                if (currentDate && !isNaN(currentDate.getTime())) {
                    hours = String(currentDate.getHours()).padStart(2, '0');
                    minutes = String(currentDate.getMinutes()).padStart(2, '0');
                }
            }
            selectedDate.setDate(selectedDate.getDate() + 1);
            const day = String(selectedDate.getDate()).padStart(2, '0');
            const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
            const year = selectedDate.getFullYear();
            this.$set(this.license, 'valid_to', `${day}.${month}.${year} ${hours}:${minutes}`);
        },

        removeModule(item) {
            const index = this.license.modules.findIndex(moduleId => {
                if (typeof moduleId === 'object') {
                    return moduleId.id === item.id;
                }
                return moduleId === item.id;
            });
            
            if (index >= 0) {
                this.license.modules.splice(index, 1);
            }
        },
        async getModules() {
            try {
                const response = await axios.get("/modulList", {
                    headers: {
                        Authorization: "Bearer " + this.$store.state.tokenData.user.access_token,
                    },
                });
                this.modulesList = response.data;
            } catch (error) {
                console.error("Error moduls:", error.response?.data || error);
                this.$emit("snackMessage", this.$t("general.error_loading_modules"), "error");
            }
        },
        async getOculus() {
            try {
                const response = await axios.get("/oculusDeviceList", {
                    headers: {
                        Authorization: "Bearer " + this.$store.state.tokenData.user.access_token,
                    },
                });
                this.oculusList = response.data;
            } catch (error) {
                console.error("Error clients:", error.response?.data || error);
                this.$emit("snackMessage", this.$t("general.error_loading_clients"), "error");
            }
        },
        async getClients() {
            if(this.$store.state.tokenData.user.user.tenant_id === "1"){
                  try {
                const response = await axios.get("/tenantList", {
                    headers: {
                        Authorization: "Bearer " + this.$store.state.tokenData.user.access_token,
                    },
                  });
                this.clientList = response.data.filter(e => e.is_active == 1);
                 } catch (error) {
                 console.error("Error clients:", error.response?.data || error);
                 this.$emit("snackMessage", this.$t("general.error_loading_clients"), "error");
                 }
            }else{
                this.clientList = [
                                   {
                                    name: this.$store.state.tokenData.user.user.name,
                                    tenant_id: this.$store.state.tokenData.user.user.tenant_id,
                                   }
                                 ];
                }
        },
        prepareLicenseData() {
            const selectedClient = this.clientList.find(client => client.id === this.license.tenant_id);
            const payload = {
                name: this.license.name,
                client: selectedClient ? selectedClient.name : '',
                client_email: selectedClient ? selectedClient.email: '',
                tenant_id: selectedClient ? `${selectedClient.tenant_id}` : '',
                valid_from: this.license.valid_from,
                valid_to: this.license.valid_to,
                devices: parseInt(this.license.devices),
                is_active: this.license.is_active,
                modules: Array.isArray(this.license.modules)
                    ? this.license.modules.map(m => typeof m === 'object' ? m.id : m)
                    : [],
                oculus_devices: Array.isArray(this.license.oculusDevices)
                    ? this.license.oculusDevices.map(o => typeof o === 'object' ? o.id : o)
                    : [],
            };
            console.log("Payload prepared for submission:", payload);
            return payload;
        },
        submit() {
            if (this.$refs.form.validate()) {
                const payload = this.prepareLicenseData();
                const request = this.isEdited
                    ? axios.put(`/updateLicense/${this.license.id}`, payload, {
                        headers: {
                            Authorization: "Bearer " + this.$store.state.tokenData.user.access_token,
                        },
                    })
                    : axios.post("/storeLicense", payload, {
                        headers: {
                            Authorization: "Bearer " + this.$store.state.tokenData.user.access_token,
                        },
                    });

                request
                    .then((response) => {
                        if (response.data.isError) {
                            this.$emit(
                                "snackMessage",
                                response.data.message || this.$t("general.error"),
                                "warning"
                            );
                        } else {
                            const successMessageKey = this.isEdited
                                ? "licenses.edit_license"
                                : "licenses.add_license";

                            if (this.isEdited) {
                                this.$emit("updateLicense", response.data.licence || response.data);
                            } else {
                                this.$emit("addLicense", response.data.licence || response.data);
                            }
                            this.$emit(
                                "snackMessage",
                                this.$t(successMessageKey),
                                "success"
                            );
                            this.$emit("refresh");
                            this.$emit("close");
                        }
                    })
                    .catch((error) => {
                        console.error("Error saving data:", error.response?.data || error.message || error);
                        const errorMessage = error.response?.data?.message || this.$t("general.error_saving_license");
                        this.$emit(
                            "snackMessage",
                            errorMessage,
                            "error"
                        );
                    });
            }
        },
        initializeFormData() {

            //  if (this._formInitialized) return;
            //   this._formInitialized = true;
            if (this.license) {
                let startDate = this.license.valid_from;
                if (!startDate && this.license.start_at) {
                    startDate = this.license.start_at;
                    this.$set(this.license, 'valid_from', startDate);
                }
                let endDate = this.license.valid_to;
                if (!endDate && this.license.finish_at) {
                    endDate = this.license.finish_at;
                    this.$set(this.license, 'valid_to', endDate);
                }
                this.datePickerStartDate = this.convertToDatePickerFormat(startDate);
                this.datePickerEndDate = this.convertToDatePickerFormat(endDate);

                console.log("Dates converted for date picker:", {
                    original_start: startDate,
                    picker_start: this.datePickerStartDate,
                    original_end: endDate,
                    picker_end: this.datePickerEndDate
                });
               

                if (!this.license.devices) {
                    this.$set(this.license, 'devices', 1);
                }
                if (this.isEdited) {
                    if (!this.license.tenant_id && this.license.client && this.clientList.length > 0) {
                        const client = this.clientList.find(c => c.name === this.license.client);
                        if (client) {
                            this.$set(this.license, 'tenant_id', client.id);
                            this.$nextTick(() => {
                                this.updateMaxDevices();
                            });
                        }
                    }
                    if (!this.license.tenant_id && this.license.name_client && this.clientList.length > 0) {
                        const client = this.clientList.find(c => c.name === this.license.name_client);
                        if (client) {
                            this.$set(this.license, 'tenant_id', client.id);
                            this.$nextTick(() => {
                                this.updateMaxDevices();
                            });
                        }
                    }
                 
                }
                console.log("que oculus", this.license.oculusDevices);
                if (!this.license.oculusDevices) {
                    this.$set(this.license, 'oculusDevices', []);
                }else  if (Array.isArray(this.license.oculusDevices) && this.license.oculusDevices.length > 0 
                        && typeof this.license.oculusDevices[0] === 'object') {
                            console.log("que cambia oculos", this.license);
                        this.$set(this.license, 'oculusDevices', this.license.oculusDevices.map(o => o.id));
                }
                console.log("que modules", this.license.modules);
                if (!this.license.modules) {
                    this.$set(this.license, 'modules', []);
                } else if (Array.isArray(this.license.modules) && this.license.modules.length > 0 
                    && typeof this.license.modules[0] === 'object') {
                        console.log("que cambia modules", this.license);
                    this.$set(this.license, 'modules', this.license.modules.map(m => m.id));
                }
            }
        }
    },
    async created() {
        if (this.$store.state.tokenData.user.user.tenant_id == '1') {
            await Promise.all([
                this.getModules(),
                this.getClients(),
                this.getOculus(),
            ]);
            this.initializeFormData();
        }
    },
};
</script>